/* eslint @ngrx/prefer-effect-callback-in-block-statement: off */

import { inject, Injectable, DOCUMENT } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { exhaustMap, filter, race, take, tap, timer } from 'rxjs';

import { isAutomatedTestSession } from '@ninety/platform/services/services/automated-test-session';
import { SegmentService } from '@ninety/platform/services/services/segment.service';

import { selectHasTier2Data } from '../../app-init/app-init.selectors';
import { appActions } from '../../app.actions';

import { ANALYTICS_DEFER_MS } from './third-party-defer';

/**
 * Defers non-critical third-party script loading until after the critical render path.
 *
 * Uses the same race(selectHasTier2Data, timer(ANALYTICS_DEFER_MS)) pattern as Intercom deferral:
 * - On authenticated routes: Tier2 data load or 3s timeout, whichever comes first
 * - On login/unauthenticated routes: 3s timer fires (Tier2 never loads)
 *
 * Scripts deferred:
 * - Google Tag Manager (GTM) — loads 12+ sub-scripts (FullStory, Aptrinsic, LinkedIn, etc.)
 * - Impact Radius — affiliate/referral tracking
 * - Segment — analytics (stub queues calls until script loads)
 *
 * None of them load for an automated sign-up session — see isAutomatedTestSession.
 */
@Injectable()
export class ThirdPartyScriptsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly document = inject(DOCUMENT);
  private readonly segmentService = inject(SegmentService);

  loadDeferredScripts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.bootstrap),
        exhaustMap(() =>
          race(this.store.select(selectHasTier2Data).pipe(filter(Boolean), take(1)), timer(ANALYTICS_DEFER_MS))
        ),
        // Automated sign-ups must reach no marketing or analytics destination. GTM matters most here:
        // it fans out to FullStory and friends, which would session-record a bot run and spend quota
        // on it. Segment self-gates as well, so it is safe either way.
        filter(() => !isAutomatedTestSession()),
        tap(() => {
          this.loadGTM();
          this.loadImpactRadius();
          this.segmentService.loadScript();
        })
      ),
    { dispatch: false }
  );

  private loadGTM(): void {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const j = this.document.createElement('script');
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-P3D96HZ';
    this.document.head.appendChild(j);
  }

  private loadImpactRadius(): void {
    const w = window as any;
    if (w.impact) return; // Already loaded by ReferralService
    w.ire_o = 'ire';
    w.ire =
      w.ire ||
      function () {
        // eslint-disable-next-line prefer-rest-params -- must match Impact Radius's expected Arguments format
        (w.ire.a = w.ire.a || []).push(arguments);
      };
    const f = this.document.createElement('script');
    f.async = true;
    f.src = 'https://utt.impactcdn.com/A5363308-38ea-4a1d-8162-6307f751fa991.js';
    this.document.head.appendChild(f);
  }
}
